import {
  STATUS_LABELS,
  INSTALL_AGE_LABELS,
  peripheralsDown,
  installAge,
  localDateString,
} from './fleetStatusUtils';

// Everything the table shows, plus the facets that are filterable on screen but
// have no column (region, market, IP set) and the two derived values a
// spreadsheet would otherwise recompute (install age, coverage). Grouped
// identity -> location -> health -> vitals, which is close to but not the same
// as the on-screen column order.

// [header, accessor(row, today)]. Order here is the column order in the file.
const COLUMNS = [
  ['Location ID', (r) => r.locationId],
  ['Name', (r) => r.prettyName],
  ['Region', (r) => r.region],
  ['Market', (r) => r.market],
  // Blank until the locations resolver returns it — blank means "no value",
  // the same as every other unknown in this file.
  ['Account tier', (r) => r.accountTier],
  ['IP set', (r) => (r.ipConfigured ? 'Yes' : 'No')],
  ['Online date', (r) => (r.onlineDate ? r.onlineDate.slice(0, 10) : '')],
  // Exported alongside the date so a spreadsheet can group on it without
  // re-deriving the 28-day rule and getting a different answer.
  [
    'Install age',
    (r, today) => INSTALL_AGE_LABELS[installAge(r.onlineDate, today)] || '',
  ],
  ['Status', (r) => STATUS_LABELS[r.status] || r.status],
  // The page reports a fridge with no IP as Not Monitored whatever the
  // evaluator said, so the evaluator's own answer ships beside it rather than
  // being lost. Identical to Status for every fridge that has an IP.
  [
    'Reported by FHMS',
    (r) => STATUS_LABELS[r.reportedStatus] || r.reportedStatus,
  ],
  ['Stocking status', (r) => r.stockingStatus],
  ['Reasons', (r) => r.reasons.join('; ')],
  // ISO 8601 UTC rather than the relative "5m ago" on screen: a spreadsheet needs
  // something sortable and unambiguous, and "5m ago" is only true at export time.
  [
    'Last heartbeat (UTC)',
    (r) =>
      r.lastHeartbeat ? new Date(r.lastHeartbeat * 1000).toISOString() : '',
  ],
  ['Temperature', (r) => r.temperature],
  // Empty means "no peripheral data", which is NOT the same as "None" — the same
  // distinction the Peripherals column makes on screen.
  [
    'Peripherals down',
    (r) => {
      const down = peripheralsDown(r);
      if (down === null) return '';
      return down.length ? down.join('; ') : 'None';
    },
  ],
  // Blank when not loaded; 0 only when we know there are none open.
  [
    'Open work orders',
    (r) => (r.activeWorkOrders ? r.activeWorkOrders.length : ''),
  ],
  [
    'Work order numbers',
    (r) =>
      r.activeWorkOrders
        ? r.activeWorkOrders
            .map((wo) => wo.wo)
            .filter(Boolean)
            .join('; ')
        : '',
  ],
  ['Jofemar', (r) => r.jofemarState],
  ['Out of order', (r) => (r.outOfOrder ? 'Yes' : 'No')],
  ['Safety hold', (r) => (r.safetyHold ? 'Yes' : 'No')],
  ['Dropouts (24h)', (r) => (r.history ? r.history.longGapCount : '')],
  [
    'Heartbeat coverage (24h)',
    (r) =>
      r.history && Number.isFinite(r.history.coverage)
        ? `${Math.round(r.history.coverage * 100)}%`
        : '',
  ],
];

function escapeCsv(value) {
  const str = value === undefined || value === null ? '' : String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Two trailing columns naming what this file is a slice of. Columns rather than
 * a preamble row: a comment line above the header breaks strict CSV readers, and
 * the filename alone is lost as soon as someone pastes the rows into a sheet.
 * Repeated per row because sorting in a spreadsheet separates a row from its
 * neighbours.
 */
const PROVENANCE = [
  ['Exported', (r, today, meta) => meta.exportedAt],
  ['Filters applied', (r, today, meta) => meta.filters],
  // The denominator. The filename used to carry it and now names the query
  // instead, so without this column a filtered slice opens with no way to tell
  // it apart from the whole fleet.
  ['Rows in scope', (r, today, meta) => meta.scope],
];

/**
 * CSV text for a set of rows. Split from the download so it's checkable.
 * @param {Object[]} rows Already filtered and sorted.
 * @param {string} today YYYY-MM-DD, for the derived install age.
 * @param {?Object} meta {exportedAt, filters}; omit for no provenance columns.
 * @returns {string}
 */
export function toCsv(rows, today = localDateString(), meta = null) {
  const columns = meta ? [...COLUMNS, ...PROVENANCE] : COLUMNS;
  const header = columns.map(([label]) => escapeCsv(label)).join(',');
  const lines = rows.map((row) =>
    columns
      .map(([, accessor]) => escapeCsv(accessor(row, today, meta)))
      .join(','),
  );
  return [header, ...lines].join('\n');
}

// Everything outside [A-Za-z0-9] becomes a single dash: chip labels are free
// display text, and `/ \ : * ? " < > |` are all illegal in a filename on some
// platform or other.
function slugPart(text) {
  return text
    .trim()
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Filenames survive about 255 bytes. One facet stays well short even fully
// selected; it is several at once — a dozen markets beside a handful of reasons
// — that overruns. The terms that fit are kept in the order the page shows
// them, and the rest are counted rather than dropped in silence.
const MAX_FILTER_CHARS = 120;

/**
 * Filename for an export, naming the query rather than the row count:
 * `fleet-status-2026-09-23-status=Live+Removed-region=Midwest.csv`.
 *
 * Chip labels arrive as "Name: Value". Values of the same facet collapse onto
 * one `key=a+b` term, because picking three statuses is one filter, not three.
 *
 * @param {string} dateStamp YYYY-MM-DD local.
 * @param {string[]} filterLabels Applied-filter chip labels, page order.
 * @returns {string}
 */
export function fleetStatusFileName(dateStamp, filterLabels = []) {
  const byFacet = new Map();
  filterLabels.forEach((label) => {
    const split = label.indexOf(': ');
    const facet = slugPart(
      split === -1 ? label : label.slice(0, split),
    ).toLowerCase();
    if (!facet) return;
    if (!byFacet.has(facet)) byFacet.set(facet, []);
    const value = split === -1 ? '' : slugPart(label.slice(split + 2));
    if (value) byFacet.get(facet).push(value);
  });

  const terms = Array.from(byFacet.entries()).map(([facet, values]) =>
    values.length ? `${facet}=${values.join('+')}` : facet,
  );

  const kept = [];
  let used = 0;
  let dropped = 0;
  terms.forEach((term) => {
    // Once one term is dropped the rest go too, so the name never skips a
    // filter and then resumes — that would read as a different query.
    if (dropped === 0 && used + term.length + 1 <= MAX_FILTER_CHARS) {
      kept.push(term);
      used += term.length + 1;
    } else {
      dropped += 1;
    }
  });
  if (dropped > 0) kept.push(`plus${dropped}more`);

  return `fleet-status-${dateStamp}${
    kept.length ? `-${kept.join('-')}` : ''
  }.csv`;
}

/**
 * Download the given rows as a CSV file.
 *
 * A filtered export says so in its name. The file carries no filter chips, and
 * IP set is filtered by default, so without this a subset would look like the
 * whole fleet to whoever opens it later.
 *
 * @param {Object[]} rows Already filtered and sorted.
 * @param {string} dateStamp YYYY-MM-DD local, so an evening export is not
 *   stamped with tomorrow's UTC date.
 * @param {number} totalRows Fridges in scope before filtering.
 * @param {string[]} filterLabels Applied-filter chip labels, in the order the
 *   page shows them. Empty means nothing was filtered.
 */
export default function downloadFleetStatusCsv(
  rows,
  dateStamp,
  totalRows,
  filterLabels = [],
) {
  const meta = {
    exportedAt: new Date().toISOString(),
    filters: filterLabels.length
      ? filterLabels.join('; ')
      : 'none (all fridges in scope)',
    // An unknown fleet size stays unknown rather than borrowing the row count.
    scope: Number.isFinite(totalRows)
      ? `${rows.length} of ${totalRows} fridges in scope`
      : `${rows.length} rows; fridges in scope unknown`,
  };
  const blob = new Blob([toCsv(rows, dateStamp, meta)], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fleetStatusFileName(dateStamp, filterLabels);
  a.click();
  URL.revokeObjectURL(url);
}
